import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FaDownload } from 'react-icons/fa';
import reportService from '../../services/reportService';
import userService from '../../services/userService';
import projectService from '../../services/projectService';
import Pagination from '../../components/Pagination';
import { formatHoursToHoursMinutes, formatDecimalHours, calculateTotalHours } from '../../utils/timeUtils';

const AdminViewReports = () => {
  const [reports, setReports] = useState([]);
  const [developers, setDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearchText, setUserSearchText] = useState('');
  const userDropdownRef = React.useRef(null);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  
  // Filter states
  const [filters, setFilters] = useState({
    userId: '',
    startDate: '',
    endDate: '',
    projectId: ''
  });
  
  // Projects for selected user
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [projectsError, setProjectsError] = useState(null);
  
  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        const developersData = await userService.getAllDevelopers();
        setDevelopers(developersData);
      } catch (err) {
        console.error('Error fetching developers:', err);
      }
    };
    
    fetchDevelopers();
  }, []);
  
  useEffect(() => {
    fetchReports(currentPage);
  }, [currentPage, filters]);

  // Close user dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectDeveloper = (devId) => {
    handleFilterChange({ target: { name: 'userId', value: devId } });
    setUserSearchText('');
    setUserDropdownOpen(false);
  };
  
  const fetchReports = async (page = currentPage) => {
    setLoading(true);
    setError(null);
    try {
      console.log('Fetching reports with filters:', filters);
      const result = await reportService.getAllReports(filters, page);
      setReports(result.reports);
      setTotalPages(result.totalPages);
      // Don't reset currentPage here as it causes the pagination to jump back
      setTotalReports(result.total);
      console.log(`Found ${result.reports.length} reports`);
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError('Failed to fetch reports: ' + (err.response?.data?.msg || err.message));
      setReports([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handlePageChange = (page) => {
    console.log('Page change requested to page:', page);
    setCurrentPage(parseInt(page, 10));
  };
  
  const handleFilterChange = async (e) => {
    const { name, value } = e.target;
    
    // Update the filter state with new value
    const updatedFilters = {
      ...filters,
      [name]: value
    };
    
    // If user filter changes, fetch their projects and reset project filter
    if (name === 'userId') {
      // Reset project filter when user changes
      updatedFilters.projectId = '';
      
      // Reset project state
      setProjects([]);
      setProjectsError(null);
      
      // If a user is selected, fetch their projects
      if (value) {
        setProjectsLoading(true);
        try {
          console.log(`Fetching projects for user ID: ${value}`);
          const userProjects = await projectService.getProjectsByUserId(value);
          console.log('Projects received:', userProjects);
          setProjects(userProjects || []);
          
          if (!userProjects || userProjects.length === 0) {
            console.log('No projects found for this user');
          }
        } catch (err) {
          console.error('Error fetching user projects:', err);
          setProjectsError('Failed to load projects');
          setProjects([]);
        } finally {
          setProjectsLoading(false);
        }
      }
    }
    
    setFilters(updatedFilters);
    setCurrentPage(1); // Reset to first page when filter changes
  };
  
  const handleExport = () => {
    reportService.exportReports(filters);
  };
  
  const clearFilters = () => {
    setUserSearchText('');
    setUserDropdownOpen(false);
    setFilters({
      userId: '',
      startDate: '',
      endDate: '',
      projectId: ''
    });
    setProjects([]);
    setCurrentPage(1);
  };
  
  return (
    <div className="page-fade-in">
    <Container>
      
          <Card className="mb-4" style={{
            border: '1px solid #EEEEEE',
            borderRadius: 14,
            overflow: 'visible',
            position: 'sticky',
            top: '75px',
            zIndex: 900
          }}>
            <Card.Body style={{ overflow: 'visible' }}>
              <Form>
                <Row>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>User</Form.Label>
                      <div className="position-relative" ref={userDropdownRef}>
                        <Form.Control
                          type="text"
                          placeholder="All Developers"
                          value={
                            userDropdownOpen
                              ? userSearchText
                              : (filters.userId
                                  ? (developers.find(d => d.id === parseInt(filters.userId))?.name || '')
                                  : '')
                          }
                          onChange={(e) => {
                            setUserSearchText(e.target.value);
                            if (!userDropdownOpen) setUserDropdownOpen(true);
                          }}
                          onFocus={() => {
                            setUserSearchText('');
                            setUserDropdownOpen(true);
                          }}
                          disabled={loading}
                          autoComplete="off"
                        />
                        {loading && (
                          <div className="position-absolute" style={{ right: '10px', top: '8px' }}>
                            <Spinner animation="border" size="sm" />
                          </div>
                        )}
                        {userDropdownOpen && !loading && (
                          <div
                            className="position-absolute w-100 bg-white shadow-sm"
                            style={{
                              top: '100%',
                              left: 0,
                              zIndex: 1000,
                              maxHeight: '350px',
                              overflowY: 'auto',
                              border: '1px solid #E0E0E0',
                              borderRadius: '8px',
                              marginTop: '2px'
                            }}
                          >
                            <div
                              className="px-3 py-2"
                              style={{ cursor: 'pointer', fontWeight: filters.userId === '' ? 600 : 400 }}
                              onMouseDown={() => handleSelectDeveloper('')}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F5F5F5'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              All Developers
                            </div>
                            {developers
                              .filter(dev => dev.name.toLowerCase().includes(userSearchText.toLowerCase()))
                              .map(dev => (
                                <div
                                  key={dev.id}
                                  className="px-3 py-2"
                                  style={{
                                    cursor: 'pointer',
                                    fontWeight: parseInt(filters.userId) === dev.id ? 600 : 400,
                                    backgroundColor: parseInt(filters.userId) === dev.id ? '#E8E0F0' : 'transparent'
                                  }}
                                  onMouseDown={() => handleSelectDeveloper(dev.id)}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F5F5F5'}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = parseInt(filters.userId) === dev.id ? '#E8E0F0' : 'transparent'}
                                >
                                  {dev.name}
                                </div>
                              ))}
                            {developers.filter(dev => dev.name.toLowerCase().includes(userSearchText.toLowerCase())).length === 0 && (
                              <div className="px-3 py-2 text-muted">No developers found</div>
                            )}
                          </div>
                        )}
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project</Form.Label>
                      <div className="position-relative">
                        <Form.Select
                          name="projectId"
                          value={filters.projectId}
                          onChange={handleFilterChange}
                          disabled={!filters.userId || projectsLoading} // Disable when loading or no user selected
                        >
                          <option value="">All Projects</option>
                          {projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </Form.Select>
                        {projectsLoading && (
                          <div className="position-absolute" style={{ right: '10px', top: '8px' }}>
                            <Spinner animation="border" size="sm" />
                          </div>
                        )}
                      </div>
                      {filters.userId && !projectsLoading && projects.length === 0 && (
                        <Form.Text className="text-danger">
                          No projects found for selected user. The user might not be assigned to any projects.
                        </Form.Text>
                      )}
                      {projectsError && (
                        <Form.Text className="text-danger">
                          {projectsError}
                        </Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={5}>
                    <Form.Group className="mb-3">
                      <Form.Label>Date Range</Form.Label>
                      <div className="d-flex gap-2">
                        <Form.Control
                          type="date"
                          name="startDate"
                          value={filters.startDate}
                          onChange={handleFilterChange}
                          style={{ flex: 1 }}
                        />
                        <Form.Control
                          type="date"
                          name="endDate"
                          value={filters.endDate}
                          onChange={handleFilterChange}
                          style={{ flex: 1 }}
                        />
                        <Button
                          variant="secondary"
                          onClick={clearFilters}
                          size="sm"
                        >
                          Clear Filter
                        </Button>
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Export to CSV</Tooltip>}
                        >
                          <Button
                            variant="success"
                            onClick={handleExport}
                            size="sm"
                            style={{ borderRadius: 50, padding: '0.3rem 0.8rem' }}
                            disabled={loading || reports.length === 0}
                          >
                            <FaDownload />
                          </Button>
                        </OverlayTrigger>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>
      
      {/* Filter Summary */}
      {(filters.userId || filters.projectId || filters.startDate || filters.endDate) && (
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Body className="py-2">
                <small className="text-muted">
                  <strong>Active Filters:</strong> {' '}
                  {filters.userId && developers.find(d => d.id === parseInt(filters.userId)) && 
                    <span className="me-3">User: {developers.find(d => d.id === parseInt(filters.userId)).name}</span>
                  }
                  {filters.projectId && projects.find(p => p.id === parseInt(filters.projectId)) && 
                    <span className="me-3">Project: {projects.find(p => p.id === parseInt(filters.projectId)).name}</span>
                  }
                  {filters.startDate && <span className="me-3">From: {filters.startDate}</span>}
                  {filters.endDate && <span className="me-3">To: {filters.endDate}</span>}
                </small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <Card style={{ border: '1px solid #EEEEEE', borderRadius: 14 }}>
            <Card.Header style={{
              background: 'linear-gradient(135deg, #E8E0F0, #D6E6F5)',
              borderBottom: 'none', borderRadius: '14px 14px 0 0',
              padding: '0.9rem 1.25rem'
            }}>
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontWeight: 700, color: '#7C6FA0', fontSize: '0.95rem' }}>Reports {totalReports > 0 ? `(${totalReports})` : ''}</span>
                {filters.projectId && (
                  <Button 
                    as={Link}
                    to={`/admin/projects/${filters.projectId}/summary?userId=${filters.userId || ''}`}
                    variant="outline-primary"
                    size="sm"
                    style={{ borderRadius: 50 }}
                  >
                    View All Project Work
                  </Button>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              {error ? (
                <div className="alert alert-danger">{error}</div>
              ) : reports.length === 0 ? (
                <div className="text-center p-4">
                  <p>No reports found with the current filters.</p>
                  <Button variant="primary" onClick={clearFilters}>
                    Clear Filters
                  </Button>
                </div>
              ) : (
                <>
                  <Table responsive striped bordered hover>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Developer</th>
                        <th>Hours Worked</th>
                        <th>Tasks</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.map(report => (
                        <tr key={report.id}>
                          <td>{new Date(report.date).toLocaleDateString()}</td>
                          <td>{report.user_name}</td>
                          <td>
                            {formatHoursToHoursMinutes(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)}
                            <div className="text-muted small">({formatDecimalHours(report.task_details && report.task_details.length > 0 ? calculateTotalHours(report.task_details) : report.hours_worked)} hours)</div>
                            {(!report.task_details || report.task_details.length === 0) && (
                              <div className="text-danger" style={{fontSize: '0.7em'}}>Task details missing - showing stored hours</div>
                            )}
                          </td>
                          <td>
                            {report.tasks.length > 80
                              ? `${report.tasks.substring(0, 800)}...`
                              : report.tasks}
                          </td>
                          <td>
                            <Button
                              as={Link}
                              to={`/admin/reports/${report.id}`}
                              variant="info"
                              size="sm"
                              className="me-2"
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  <div className="pagination-wrapper">
                    <Pagination
                      itemsPerPage={10}
                      totalItems={totalReports}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
    </div>
  );
};

export default AdminViewReports;
